import React, { memo, useEffect, useCallback } from 'react';
import type { StepNode } from '@segmently/schema-models/v2';
import type { OnActionCallback, OnEventCallback } from '../../../types/events';
import type { TemplateVariables } from '../../../utils/contentResolver';
import type { PreviewMode } from '../../../types/previewMode';
import { createSliderCycleCompleteEvent } from '../../../utils/eventEmitter';
import { useProgressAnimation, type ProgressAnimationItem } from '../../../hooks';
import { useElementAnimation, mergeAnimationClassName } from '../../../animations';

import { Background } from '../../shared/Background';
import { HeaderBar } from '../../shared/HeaderBar';
import { ProgressBar } from '../../shared/ProgressBar';
import styles from './ProgressSteps.module.css';

export interface ProgressStepsProps {
  step: StepNode;
  locale: string;
  variables?: TemplateVariables;
  resolvedScreenValues?: Record<string, string>;
  onAction?: OnActionCallback;
  onEvent?: OnEventCallback;
  progress?: number;
  editMode?: boolean;
  previewMode?: PreviewMode;
  isAnimationPlaying?: boolean;
  onAnimationComplete?: () => void;
}

export const ProgressSteps = memo(function ProgressSteps({
  step,
  locale,
  variables,
  resolvedScreenValues: _resolvedScreenValues,
  onAction,
  onEvent: _onEvent,
  progress: externalProgress = 0,
  editMode = false,
  previewMode,
  isAnimationPlaying = false,
  onAnimationComplete,
}: ProgressStepsProps): React.ReactElement {
  const { content } = step;

  const rawItems = content?.stepper?.items ?? [];
  const timerDuration = content?.stepper?.timer?.duration ?? 15;
  const timerDurationMs = timerDuration * 1000;

  // V2: StepperSegment uses progressStart/progressEnd range instead of durationPercent
  const animationItems: ProgressAnimationItem[] = rawItems.map((item) => ({
    durationPercent: ((item.progressEnd ?? 0) - (item.progressStart ?? 0)) || undefined,
  }));

  const handleAnimationComplete = useCallback(() => {
    if (onAction) {
      onAction(createSliderCycleCompleteEvent('progressbar-cycle-complete', step, 'progress-bar'));
    }
    onAnimationComplete?.();
  }, [onAction, onAnimationComplete, step]);

  const shouldAutoStart = !editMode && previewMode !== 'static';

  const {
    percent,
    itemIndex,
    isPlaying,
    isComplete,
    play,
    pause,
    restart,
  } = useProgressAnimation({
    duration: timerDurationMs,
    items: animationItems,
    autoStart: shouldAutoStart,
    onComplete: handleAnimationComplete,
  });

  useEffect(() => {
    if (previewMode === 'static') return;
    if (!editMode) return;

    if (isAnimationPlaying && !isPlaying) {
      if (isComplete) {
        restart();
      } else {
        play();
      }
    } else if (!isAnimationPlaying && isPlaying) {
      pause();
    }
  }, [isAnimationPlaying, isPlaying, isComplete, play, pause, restart, previewMode, editMode]);

  const isAnimating = isPlaying || isAnimationPlaying;
  const hasAnimationStarted = percent > 0 || isPlaying || isComplete;
  const displayProgress = (isAnimating || hasAnimationStarted) ? percent : externalProgress;
  const displayItemIndex = (isAnimating || hasAnimationStarted) ? itemIndex : 0;

  const ep = editMode || previewMode === 'live' ? 'content' : undefined;
  const stepperKind = content?.stepper?.kind;
  // Screen entrance slot `stepper` (F14 / AMV-AC-038): the progress bar + steps block enters after the header
  const stepperAnim = useElementAnimation(ep ? `${ep}.stepper` : undefined);

  return (
    <div
      className={`${styles.container}${previewMode === 'live' ? ` ${styles.liveMode}` : ''}${editMode ? ' screen-view--edit-mode' : ''}`}
      data-testid="view-progress-steps"
    >

      <Background
        backdrop={content?.canvas?.background}
        locale={locale}
        editPath={ep ? `${ep}.canvas.background` : undefined}
        previewMode={previewMode}
      />

      <HeaderBar
        header={content?.header}
        locale={locale}
        variables={variables}
        step={step}
        onAction={onAction}
        editPath={ep ? `${ep}.header` : undefined}
      />

      <div
        className={mergeAnimationClassName(`${styles.content} ${styles.xPaddings}`, stepperAnim)}
        style={stepperAnim.style}
        data-anim={stepperAnim['data-anim']}
      >
        {stepperKind === 'orbital' ||
        stepperKind === 'imageContainerFirst' ||
        stepperKind === 'chartContainerFirst' ? (
          <ProgressBar
            stepper={content?.stepper}
            progress={displayProgress}
            locale={locale}
            variables={variables}
            previewMode={previewMode}
            className={styles.progressBarFull}
            data-testid="progress-bar"
            editPath={ep ? `${ep}.stepper` : undefined}
            currentSegmentIndex={displayItemIndex}
          />
        ) : (
          <div className={styles.progressBarWrapper}>
            <ProgressBar
              stepper={content?.stepper}
              progress={displayProgress}
              locale={locale}
              variables={variables}
              previewMode={previewMode}
              className={styles.progressBar}
              data-testid="progress-bar"
              editPath={ep ? `${ep}.stepper` : undefined}
              currentSegmentIndex={displayItemIndex}
            />
          </div>
        )}
      </div>
    </div>
  );
});

ProgressSteps.displayName = 'ProgressSteps';
